import { LoadingButton } from '@mui/lab';
import { Box, Button, Link, TextField, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';
import ForaDoHorarioAtendimento from '../../../components/ForaDoHorarioAtendimento';
import { sendValidacao } from '../../../services/checkout-service/sendValidacao';

const StyledLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.primary.main,
  fontWeight: 'bold',
  fontSize: '1rem',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const validationSchema = Yup.object({
  codigoValidador: Yup.string()
    .length(4, 'O código deve ter exatamente 4 dígitos')
    .required('Código validador é obrigatório'),
  numeroSerie: Yup.string()
    .length(6, 'O número de série deve ter exatamente 6 dígitos')
    .required('Número de série é obrigatório'),
});

export default function CodigoVerificacao({ navigate }) {
  const theme = useTheme();
  const [erroMensagem, setErroMensagem] = useState('');
  const disableIsWorking = !process.env.REACT_APP_ENABLE_IS_WORKING;

  const handleEnviarCodigo = async (values, { setSubmitting }) => {
    try {
      setErroMensagem(''); // Resetando mensagem de erro
      const resp = await sendValidacao(values.codigoValidador, values.numeroSerie);

      if (resp && resp.status === 200) {
        localStorage.setItem(
          'tecnozap_user_session',
          JSON.stringify({
            ...values,
            trustedVci: true,
            validatedAt: new Date().toISOString(),
          })
        );
        navigate(3);
      }
    } catch (error) {
      console.error('Erro na API:', error);
      if (error.response && error.response.status === 406) {
        setErroMensagem('Erro no código ou número de série inválido');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        textAlign: 'center',
      }}
    >
      <ForaDoHorarioAtendimento />

      <Typography
        variant="h5"
        component="h3"
        sx={{ color: theme.palette.error.main, mb: 3 }}
      >
        Você está offline
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }}>
        Informe o código validador e o número de série para iniciar o atendimento.
      </Typography>

      {erroMensagem && (
        <Typography variant="body1" color="error" sx={{ mb: 2 }}>
          {erroMensagem}
        </Typography>
      )}

      <Formik
        initialValues={{ codigoValidador: '', numeroSerie: '' }}
        validationSchema={validationSchema}
        onSubmit={handleEnviarCodigo}
      >
        {({ isSubmitting, errors, touched, setFieldValue, values }) => (
          <Form style={{ display: 'flex', height: '100%', flexDirection: 'column' }}>
            <Typography
              variant="body1"
              component="label"
              htmlFor="codigoValidador"
              sx={{
                display: 'block',
                textAlign: 'center',
                mb: 1,
                fontWeight: 'bold',
              }}
            >
              Código validador
            </Typography>
            <Field
              as={TextField}
              id="codigoValidador"
              name="codigoValidador"
              placeholder="Ex: 0000"
              variant="outlined"
              size="medium"
              fullWidth
              sx={{
                mb: 1,
                borderColor:
                  errors.codigoValidador && touched.codigoValidador ? 'red' : '',
              }}
              inputProps={{ maxLength: 4 }}
              error={errors.codigoValidador && touched.codigoValidador}
              value={values.codigoValidador}
              onChange={(e) => {
                setFieldValue('codigoValidador', e.target.value);
                setErroMensagem(''); // Reseta a mensagem de erro
              }}
            />
            <ErrorMessage
              name="codigoValidador"
              component="div"
              style={{ color: 'red', marginBottom: '10px' }}
            />

            <Typography
              variant="body1"
              component="label"
              htmlFor="numeroSerie"
              sx={{
                display: 'block',
                textAlign: 'center',
                mb: 1,
                fontWeight: 'bold',
              }}
            >
              Número de série
            </Typography>
            <Field
              as={TextField}
              id="numeroSerie"
              name="numeroSerie"
              placeholder="Número de série"
              variant="outlined"
              size="medium"
              fullWidth
              sx={{
                mb: 1,
                borderColor: errors.numeroSerie && touched.numeroSerie ? 'red' : '',
              }}
              inputProps={{ maxLength: 6 }}
              error={errors.numeroSerie && touched.numeroSerie}
              value={values.numeroSerie}
              onChange={(e) => {
                setFieldValue('numeroSerie', e.target.value);
                setErroMensagem(''); // Reseta a mensagem de erro
              }}
            />
            <ErrorMessage
              name="numeroSerie"
              component="div"
              style={{ color: 'red', marginBottom: '10px' }}
            />

            <Box sx={{ display: 'flex', flex: 1, alignItems: 'end', mt: 3 }}>
              <Button
                variant="outlined"
                sx={{
                  fontSize: 16,
                  width: '100%',
                  mr: 1,
                  height: 48,
                }}
                onClick={() => navigate(0)}
              >
                Voltar
              </Button>

              <LoadingButton
                loading={isSubmitting}
                disabled={isSubmitting}
                type="submit"
                variant="contained"
                sx={{
                  width: '100%',
                  ml: 1,
                  height: 48,
                  m: 0,
                }}
              >
                Enviar
              </LoadingButton>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
